import * as React from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { isCourseFrameEnabled, isCourseFramePhase3Enabled } from 'bundles/course-frame/utils/epicUtils';
import CourseMembershipStore from 'bundles/in-course-stores/stores/CourseMembershipStore';
import type { Item } from 'bundles/learner-progress/types/Item';

export type CourseFrameContextType = {
  /**
   * The enrollment time of the current user into the current course. We occasionally need to use this to target users for experiments.
   */
  enrollmentTime?: number;
  /**
   * Whether the Course Frame UX should be rendered for the current user and item.
   * Is mostly used to correctly display nested NavItems between the old Item Page and new Course Frame.
   */
  isCourseFrameEnabled: boolean;
  isPhase3Enabled: boolean;
  isDesktopNavOpen: boolean;
  isMobileNavOpen: boolean;
  setIsDesktopNavOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMobileNavOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

type PropsFromUseFluxibleStore = {
  enrollmentTime: number | undefined;
};

export const CourseFrameContext = createContext<CourseFrameContextType | undefined>(undefined);

type Props = React.PropsWithChildren<{
  computedItem: Item;
}>;

/**
 * Provides the Course Frame context to the children.
 */
export const CourseFrameContextProvider = (props: Props) => {
  const { children, computedItem } = props;
  const { enrollmentTime } = useFluxibleStore(
    CourseMembershipStore,
    (courseMembershipStore): PropsFromUseFluxibleStore => ({
      enrollmentTime: courseMembershipStore.enrollmentTime(),
    })
  );

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isDesktopNavOpen, setIsDesktopNavOpen] = useState(true);

  const courseFrameEnabled = isCourseFrameEnabled({ computedItem });

  const isPhase3Enabled = useMemo(() => isCourseFramePhase3Enabled(enrollmentTime), [enrollmentTime]);

  const contextValue: CourseFrameContextType = useMemo(
    () => ({
      enrollmentTime,
      isCourseFrameEnabled: courseFrameEnabled,
      isPhase3Enabled,
      isDesktopNavOpen,
      isMobileNavOpen,
      setIsDesktopNavOpen,
      setIsMobileNavOpen,
    }),
    [
      enrollmentTime,
      courseFrameEnabled,
      isPhase3Enabled,
      isDesktopNavOpen,
      isMobileNavOpen,
      setIsDesktopNavOpen,
      setIsMobileNavOpen,
    ]
  );

  return <CourseFrameContext.Provider value={contextValue}>{children}</CourseFrameContext.Provider>;
};

export const useCourseFrameContext = (): CourseFrameContextType => {
  const context = useContext(CourseFrameContext);
  if (!context) {
    throw new Error('useCourseFrameContext must be used within an CourseFrameContextProvider');
  }
  return context;
};
